/** Versioned company terms. Existing Stripe subscriptions keep their original version. */
export const COMPANY_PLAN_PRICING_VERSION = 'company_included_seats_v1' as const;
export const LEGACY_COMPANY_PRICING_VERSION = 'legacy_all_seats_v1' as const;
export type CompanyPlanPricingVersion = typeof COMPANY_PLAN_PRICING_VERSION | typeof LEGACY_COMPANY_PRICING_VERSION;
export const COMPANY_PLAN_INCLUDED_SEATS = 4;
export const COMPANY_PLAN_MAX_SEATS = 10000;
export const COMPANY_PLAN_REQUIRED_MESSAGE = 'Open Subscribe and activate your company plan before submitting quotes or accepting work. Company setup and billing remain available.';

/**
 * Documentation / company-plan base fee by lane.
 * Contractors keep the approved $500 / $5,000 terms.
 * Operators pay double contractors (Cipher-locked). Amounts are code constants used for
 * dynamic Stripe Prices; optional static Price IDs:
 *   STRIPE_BUSINESS_DOC_FEE_MONTHLY_PRICE_ID / YEARLY (contractor — existing)
 *   STRIPE_BUSINESS_DOC_FEE_OPERATOR_MONTHLY_PRICE_ID / YEARLY (operator — Cipher pastes)
 * Cipher Stripe Dashboard: create Products/Prices at these amounts if using static IDs.
 */
export type CompanyPlanLane = 'contractor' | 'operator';

export const CONTRACTOR_DOC_FEE_MONTHLY_CENTS = 50000; // $500/mo
export const CONTRACTOR_DOC_FEE_YEARLY_CENTS = 500000; // $5,000/yr
/** Operator documentation fee — higher than contractor. Cipher may adjust. */
export const OPERATOR_DOC_FEE_MONTHLY_CENTS = 100000; // $1,000/mo
export const OPERATOR_DOC_FEE_YEARLY_CENTS = 1000000; // $10,000/yr

export function companyPlanDocFeeCents(
  billingCycle: 'monthly' | 'yearly',
  lane: CompanyPlanLane = 'contractor',
): number {
  if (lane === 'operator') {
    return billingCycle === 'yearly' ? OPERATOR_DOC_FEE_YEARLY_CENTS : OPERATOR_DOC_FEE_MONTHLY_CENTS;
  }
  return billingCycle === 'yearly' ? CONTRACTOR_DOC_FEE_YEARLY_CENTS : CONTRACTOR_DOC_FEE_MONTHLY_CENTS;
}

export function companyPlanDocFeeLabel(
  billingCycle: 'monthly' | 'yearly',
  lane: CompanyPlanLane = 'contractor',
): string {
  const cents = companyPlanDocFeeCents(billingCycle, lane);
  const dollars = (cents / 100).toLocaleString('en-US');
  const who = lane === 'operator' ? 'operator ' : '';
  return billingCycle === 'yearly'
    ? `$${dollars}/yr ${who}company plan`.replace('  ', ' ')
    : `$${dollars}/mo ${who}company plan`.replace('  ', ' ');
}

export function companyPlanDocFeeCheckoutName(
  _billingCycle: 'monthly' | 'yearly',
  lane: CompanyPlanLane = 'contractor',
): string {
  const who = lane === 'operator' ? 'Operator' : 'Company';
  return `ZiaWellNav ${who} Plan — 4 seats included`;
}

/**
 * Resolve operator vs contractor company for plan pricing.
 * Operator register / bid-access / op-email principals → operator lane.
 */
export function resolveCompanyPlanLane(company: {
  bid_access?: string | null;
  telegram_user_id?: string | null;
} | null | undefined): CompanyPlanLane {
  const bid = String(company?.bid_access || '').trim();
  if (bid === 'operator_free' || bid === 'operator_pending') return 'operator';
  const uid = String(company?.telegram_user_id || '').trim();
  if (uid.startsWith('op-email-') || uid.startsWith('operator-test-')) return 'operator';
  return 'contractor';
}

export function companyPlanPricingVersion(value: unknown): CompanyPlanPricingVersion {
  return value === COMPANY_PLAN_PRICING_VERSION ? COMPANY_PLAN_PRICING_VERSION : LEGACY_COMPANY_PRICING_VERSION;
}

/** Rates are selected by total company capacity, not by the extra-seat quantity. */
export function calculateCompanyPlanPrice(
  totalSeats: number,
  billingCycle: 'monthly' | 'yearly',
  pricingVersion: CompanyPlanPricingVersion = COMPANY_PLAN_PRICING_VERSION,
  lane: CompanyPlanLane = 'contractor',
) {
  const includedSeats = pricingVersion === COMPANY_PLAN_PRICING_VERSION ? COMPANY_PLAN_INCLUDED_SEATS : 0;
  const minimum = includedSeats || 5;
  if (!Number.isSafeInteger(totalSeats) || totalSeats < minimum || totalSeats > COMPANY_PLAN_MAX_SEATS
    || !['monthly', 'yearly'].includes(billingCycle)) return null;
  const tiers = [
    { tierId: 'starter', planId: 'business_starter', tierName: 'Starter', max: 25, cents: 700 },
    { tierId: 'team', planId: 'business_team', tierName: 'Team', max: 100, cents: 600 },
    { tierId: 'fleet', planId: 'business_fleet', tierName: 'Fleet', max: 250, cents: 500 },
    { tierId: 'enterprise', planId: 'business_enterprise', tierName: 'Enterprise', max: COMPANY_PLAN_MAX_SEATS, cents: 400 },
  ] as const;
  const tier = tiers.find((item) => totalSeats <= item.max)!;
  const billableSeats = totalSeats - includedSeats;
  const perSeatCents = tier.cents * (billingCycle === 'yearly' ? 10 : 1);
  const baseFeeCents = companyPlanDocFeeCents(billingCycle, lane);
  const seatSubtotalCents = billableSeats * perSeatCents;
  return {
    pricingVersion, includedSeats, billableSeats, totalSeats,
    tierId: tier.tierId, planId: tier.planId, tierName: tier.tierName,
    billingCycle, perSeatCents, seatSubtotalCents, baseFeeCents,
    totalCents: baseFeeCents + seatSubtotalCents,
    interval: billingCycle === 'yearly' ? 'year' as const : 'month' as const,
    companyLane: lane,
    documentationFeeLabel: companyPlanDocFeeLabel(billingCycle, lane),
  };
}
